import React, { useRef, useState } from 'react';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { signInWithGoogleCalendar, getGoogleAccessTokenFromCredential } from '../lib/googleAuth';
import { listUpcomingCalendarEvents } from '../utils/calendarSync';
import {
  getSignInMethodsForEmail,
  googleCredentialFromAuthError,
  isGoogleOnlySignIn,
  linkEmailPasswordToUser,
  emailsMatchForLinking,
  signInWithPasswordAndLinkGoogle,
} from '../lib/authAccountLinking';
import AccountLinkingPanel from './auth/AccountLinkingPanel';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Alert,
  Link,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AppBrandLogo from './AppBrandLogo';
import LoadingLockScreen from './LoadingLockScreen';

const Login = ({ onToggle }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [mfaStep, setMfaStep] = useState('credentials');
  const [mfaCode, setMfaCode] = useState('');
  const mfaResolverRef = useRef(null);
  const mfaTotpUidRef = useRef(null);

  /** @type {[null | { type: 'google-first', email: string, password: string } | { type: 'password-first', email: string, googleCredential: import('firebase/auth').AuthCredential }, Function]} */
  const [linkFlow, setLinkFlow] = useState(null);

  const navigateAfterUserDoc = async (signedInUser) => {
    const userDocRef = doc(db, 'users', signedInUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.role === 'super_admin') {
        navigate('/admin');
      } else if (userData.role === 'company_admin') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  const resetMfaFlow = () => {
    mfaResolverRef.current = null;
    mfaTotpUidRef.current = null;
    setMfaCode('');
    setMfaStep('credentials');
  };

  const cancelLinkFlow = () => {
    setLinkFlow(null);
    setError(null);
  };

  const startGoogleFirstLink = (linkEmail, linkPassword) => {
    resetMfaFlow();
    setLinkFlow({ type: 'google-first', email: linkEmail.trim(), password: linkPassword });
    setError(null);
  };

  const shouldOfferGooglePasswordLink = async (err, linkEmail) => {
    if (err.code === 'auth/account-exists-with-different-credential') {
      return true;
    }
    if (
      err.code === 'auth/invalid-credential' ||
      err.code === 'auth/wrong-password' ||
      err.code === 'auth/user-not-found'
    ) {
      const methods = await getSignInMethodsForEmail(linkEmail);
      return isGoogleOnlySignIn(methods);
    }
    return false;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    resetMfaFlow();
    cancelLinkFlow();

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await navigateAfterUserDoc(userCredential.user);
    } catch (err) {
      console.error('Error logging in:', err);
      if (err.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(auth, err);
          const totpHint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
          if (!totpHint) {
            setError('Your account requires MFA, but no supported authenticator factor was found. Contact support.');
            return;
          }
          mfaResolverRef.current = resolver;
          mfaTotpUidRef.current = totpHint.uid;
          setMfaStep('mfa');
          setMfaCode('');
        } catch (resolverErr) {
          console.error('MFA resolver:', resolverErr);
          setError(resolverErr.message || 'Could not start two-factor sign-in.');
        }
      } else if (await shouldOfferGooglePasswordLink(err, email)) {
        startGoogleFirstLink(email, password);
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Try again later.');
      } else {
        setError(err.message || 'Sign-in failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLinkVerify = async () => {
    if (!linkFlow || linkFlow.type !== 'google-first') return;
    setError(null);
    setSubmitting(true);
    try {
      const userCredential = await signInWithGoogleCalendar();
      if (!emailsMatchForLinking(userCredential.user.email, linkFlow.email)) {
        await signOut(auth);
        setError(`Please sign in with Google using ${linkFlow.email}.`);
        return;
      }
      await linkEmailPasswordToUser(
        userCredential.user,
        linkFlow.email,
        linkFlow.password,
      );
      const accessToken = getGoogleAccessTokenFromCredential(userCredential);
      if (accessToken) {
        try {
          await listUpcomingCalendarEvents(accessToken, { maxResults: 1 });
        } catch (calendarErr) {
          console.warn('Calendar API probe (non-blocking):', calendarErr);
        }
      }
      setLinkFlow(null);
      await navigateAfterUserDoc(userCredential.user);
    } catch (err) {
      console.error('Google link verify:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Try again when ready.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 6 characters.');
      } else if (err.code === 'auth/credential-already-in-use') {
        setError('This password is already linked to another account.');
      } else {
        setError(err.message || 'Could not link your password. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordLinkVerify = async () => {
    if (!linkFlow || linkFlow.type !== 'password-first') return;
    if (!password) {
      setError('Enter your password to continue.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const userCredential = await signInWithPasswordAndLinkGoogle(
        linkFlow.email,
        password,
        linkFlow.googleCredential,
      );
      setLinkFlow(null);
      await navigateAfterUserDoc(userCredential.user);
    } catch (err) {
      console.error('Password link verify:', err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid password. Enter the password for this email account.');
      } else {
        setError(err.message || 'Could not link Google sign-in.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    const resolver = mfaResolverRef.current;
    const totpUid = mfaTotpUidRef.current;
    const code = mfaCode.replace(/\s/g, '');
    if (!resolver || !totpUid) {
      setError('Session expired. Enter your email and password again.');
      resetMfaFlow();
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpUid, code);
      const userCredential = await resolver.resolveSignIn(assertion);
      resetMfaFlow();
      await navigateAfterUserDoc(userCredential.user);
    } catch (err) {
      console.error('MFA sign-in:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid or expired code. Try a new code from your app.');
      } else {
        setError(err.message || 'Verification failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToCredentials = () => {
    resetMfaFlow();
    cancelLinkFlow();
    setError(null);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setSubmitting(true);
    cancelLinkFlow();
    try {
      const userCredential = await signInWithGoogleCalendar();
      const accessToken = getGoogleAccessTokenFromCredential(userCredential);
      if (accessToken) {
        try {
          await listUpcomingCalendarEvents(accessToken, { maxResults: 1 });
        } catch (calendarErr) {
          console.warn('Calendar API probe (non-blocking):', calendarErr);
        }
      }
      await navigateAfterUserDoc(userCredential.user);
    } catch (err) {
      console.error('Google sign-in:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        const pendingEmail = err.customData?.email || email;
        const googleCredential = googleCredentialFromAuthError(err);
        if (googleCredential && pendingEmail) {
          setLinkFlow({
            type: 'password-first',
            email: pendingEmail,
            googleCredential,
          });
          setEmail(pendingEmail);
          setError(null);
        } else {
          setError('An account already exists with this email using email and password. Sign in with your password to link Google.');
        }
      } else {
        setError(err.message || 'Google sign-in failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 4 }}>
      <LoadingLockScreen open={submitting} />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <AppBrandLogo maxWidth={300} sx={{ mb: 2 }} />
        <Typography component="h2" variant="h5">
          Login
        </Typography>

        {linkFlow ? (
          <AccountLinkingPanel
            variant={linkFlow.type === 'google-first' ? 'google-first' : 'password-first'}
            email={linkFlow.email}
            onGoogleVerify={handleGoogleLinkVerify}
            onPasswordLink={handlePasswordLinkVerify}
            onCancel={cancelLinkFlow}
            submitting={submitting}
            error={error}
          >
            {linkFlow.type === 'password-first' && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Account password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            )}
          </AccountLinkingPanel>
        ) : mfaStep === 'credentials' ? (
          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email-login"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password-login"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={submitting}>
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
            <Divider sx={{ my: 2 }}>or</Divider>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              disabled={submitting}
              onClick={handleGoogleSignIn}
            >
              Sign in with Google (Calendar access)
            </Button>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
              <Link component={RouterLink} to="/reset" variant="body2">
                Forgot password?
              </Link>
              <Link href="#" variant="body2" onClick={onToggle}>
                {"Don't have an account? Sign Up"}
              </Link>
            </Stack>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleMfaSubmit} sx={{ mt: 2, width: '100%' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Two-factor authentication is enabled for this account. Open your authenticator app and enter the
              current 6-digit code.
            </Alert>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Authenticator code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
              autoFocus
              placeholder="000000"
            />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 1 }} disabled={submitting}>
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Verify & sign in'}
            </Button>
            <Button type="button" fullWidth variant="text" onClick={handleBackToCredentials} disabled={submitting}>
              Back to email & password
            </Button>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Login;
